// @flow
import React, { Component, Fragment } from 'react';
import BannerHead from 'components/banner/BannerHead';
import BannerContent from 'components/banner/BannerContent';
import BannerTags from 'components/banner/BannerTags';
import { BannersActions, BaseActions, CommonActions, DisplayActions } from 'store/actionCreators';
import { connect } from 'react-redux';
import type { State } from 'store';
import type { BannerData, TocItem } from 'store/modules/banners';
import BannerToc from 'components/banner/BannerToc';
import QuestionModal from 'components/common/QuestionModal/QuestionModal';
import { type ContextRouter, type Location, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { convertToPlainText } from 'lib/common';
import ScrollToTop from 'components/common/ScrollToTop';
import BannerPlaceholder from 'components/banner/BannerPlaceholder';
import throttle from 'lodash/throttle';
import BannerLeftSticker from 'components/banner/BannerLeftSticker/BannerLeftSticker';
import BannerSeriesInfo from 'components/banner/BannerSeriesInfo/BannerSeriesInfo';

type Props = {
  username: ?string,
  urlSlug: ?string,
  banner: ?BannerData,
  toc: ?(TocItem[]),
  activeHeading: ?string,
  likeInProcess: boolean,
  currentUsername: ?string,
  askRemove: boolean,
  routerHistory: Location[],
  shouldCancel: boolean,
  logged: boolean,
} & ContextRouter;

class BannerViewer extends Component<Props> {
  onActivateHeading = throttle((headingId: string) => {
    BannersActions.activateHeading(headingId);
  }, 250);

  initialize = async () => {
    const { username, urlSlug, shouldCancel } = this.props;
    if (!username || !urlSlug) return;
    try {
      if (shouldCancel) return;
      await BannersActions.readBanner({
        username,
        urlSlug,
      });
      if (!this.props.banner) return;
      const { id } = this.props.banner;
      BannersActions.getSequences(id);
    } catch (e) {
      console.log(e);
      if (e && e.response && e.response.status === 404) {
        this.props.history.replace('/404');
      }
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.urlSlug !== this.props.urlSlug) {
      BannersActions.unloadBanner();
      this.initialize();
    }
  }

  componentWillUnmount() {
    BannersActions.unloadBanner();
  }

  onSetToc = (toc: ?(TocItem[])) => {
    BannersActions.setToc(toc);
  };

  onToggleLike = () => {
    const { banner, likeInProcess } = this.props;
    if (likeInProcess) return;
    if (!banner) return;
    if (banner.liked) {
      BannersActions.unlike(banner.id);
    } else {
      BannersActions.like(banner.id);
    }
  };

  onToggleAskRemove = () => {
    BannersActions.toggleAskRemove();
  };

  onConfirmRemove = async () => {
    const { banner, history, routerHistory } = this.props;
    BannersActions.toggleAskRemove();
    if (!banner) return;
    try {
      await CommonActions.removeBanner(banner.id);
      DisplayActions.flushList();
    } catch (e) {
      console.log(e);
    }
    if (routerHistory.length === 0) {
      history.push('/');
      return;
    }
    history.goBack();
  };

  componentDidMount() {
    this.initialize();
    const { hash } = this.props.location;
    if (hash !== '') {
      BannersActions.activateHeading(decodeURI(hash.split('#')[1]));
    }
  }

  informCopy = () => {
    BaseActions.showToast({
      message: '링크가 복사되었습니다',
      type: 'success',
    });
  };

  render() {
    const { banner, toc, activeHeading, username, currentUsername, askRemove, logged } = this.props;
    const { onSetToc, onActivateHeading } = this;

    if (!banner) return <BannerPlaceholder />;
    if (!banner.user) return <BannerPlaceholder />;
    const plainTextBody = convertToPlainText(banner.body);

    const url = `${process.env.BASE_URL}/@${banner.user.username}/banners/${banner.url_slug}`;
    const baseTitle = `${banner.title} | 캐시바이`;
    return (
      <Fragment>
        <Helmet>
          <title>{baseTitle}</title>
          <meta name="description" content={plainTextBody} />
          <link rel="canonical" href={url} />
          <meta property="og:url" content={url} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={baseTitle} />
          <meta property="og:description" content={plainTextBody} />
          {banner.thumbnail && <meta property="og:image" content={banner.thumbnail} />}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={baseTitle} />
          <meta name="twitter:description" content={plainTextBody} />
          {banner.thumbnail && <meta name="twitter:image" content={banner.thumbnail} />}
        </Helmet>
        <BannerLeftSticker
          logged={logged}
          likes={banner.likes}
          liked={banner.liked}
          onToggleLike={this.onToggleLike}
          url={this.props.match.url}
          title={banner.title}
          username={banner.user.username}
          informCopy={this.informCopy}
          disabled={!logged}
        />
        <BannerToc
          toc={toc}
          activeHeading={activeHeading}
          onActivateHeading={this.onActivateHeading}
        />
        <BannerHead
          id={banner.id}
          date={banner.released_at}
          title={banner.title}
          tags={banner.tags}
          categories={banner.categories}
          user={banner.user}
          likes={banner.likes}
          liked={banner.liked}
          onToggleLike={this.onToggleLike}
          ownBanner={currentUsername === username}
          onAskRemove={this.onToggleAskRemove}
          logged={logged}
          isPrivate={banner.is_private}
          url={this.props.match.url}
          informCopy={this.informCopy}
        />
        {banner.series && <BannerSeriesInfo series={banner.series} username={username || ''} />}
        <BannerContent
          thumbnail={banner.thumbnail}
          body={banner.body}
          onSetToc={onSetToc}
          onActivateHeading={onActivateHeading}
          theme={banner.meta.code_theme}
        />
        <BannerTags tags={banner.tags} />
        <QuestionModal
          open={askRemove}
          title="배너 삭제"
          description="이 배너를 정말로 삭제하시겠습니까?"
          confirmText="삭제"
          onConfirm={this.onConfirmRemove}
          onCancel={this.onToggleAskRemove}
        />
      </Fragment>
    );
  }
}

export default connect(
  ({ banners, pender, user, common }: State) => ({
    currentUsername: user.user && user.user.username,
    banner: banners.banner,
    toc: banners.toc,
    activeHeading: banners.activeHeading,
    likeInProcess: pender.pending['banners/LIKE'] || pender.pending['banners/UNLIKE'],
    askRemove: banners.askRemove,
    routerHistory: common.router.history,
    shouldCancel: common.ssr && !common.router.altered,
    logged: !!user.user,
  }),
  () => ({}),
)(withRouter(BannerViewer));



// WEBPACK FOOTER //
// ./src/containers/banner/BannerViewer.js